import React, { useEffect } from 'react';
import { IconButton } from '@atlaskit/button/new';
import { Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { SingleSelectChartType } from '@atlassian/jira-issue-field-statistics/src/common/types.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	CLEAR_ALL_QUERY_PARAMS,
	SELECTED_FIELD_ID_KEY,
	SELECTED_CHART_TYPE_KEY,
} from '../../common/constants.tsx';
import { useQueryParams } from '../../services/use-query-params/index.tsx';
import { useStatisticsStorage } from '../../services/use-statistics-storage/index.tsx';
import { FeedbackButton } from './feedback-button/index.tsx';
import messages from './messages.tsx';
import { ShareButton } from './share-button/index.tsx';
import { StatisticsRenderer } from './statistics-renderer/index.tsx';

type SelectFieldOption = { label: string; value: string };

type Props = {
	selectableFields: BusinessIssueField[];
	jql: string;
	onClose: () => void;
};

export const StatisticsSidebar = ({ selectableFields, jql, onClose }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selectedFieldId, setSelectedFieldId] = useStatisticsStorage(SELECTED_FIELD_ID_KEY);
	const [selectedChartType, setSelectedChartType] = useStatisticsStorage(SELECTED_CHART_TYPE_KEY);

	const [, setParams] = useQueryParams();

	const selectedField = selectableFields.find((field) => field.id === selectedFieldId);

	const selectFieldValue = selectedField
		? { label: selectedField.name, value: selectedField.id }
		: null;
	const selectFieldOptions = selectableFields.map((field) => ({
		label: field.name,
		value: field.id,
	}));

	useEffect(() => {
		const startTime = Date.now();
		return () => {
			const endTime = Date.now();
			const duration = endTime - startTime;
			fireUIAnalytics(createAnalyticsEvent({}), 'issueFieldStatisticsSidebar open', { duration });
		};
	}, [createAnalyticsEvent]);

	// Remove sidebar and selected field id from query params when the component unmounts
	useEffect(() => {
		return () => {
			setParams(CLEAR_ALL_QUERY_PARAMS, 'replace');
		};
	}, [setParams]);

	const onSelectField = (option: SelectFieldOption | null) => {
		if (!option) {
			setSelectedFieldId(undefined);
		} else {
			setSelectedFieldId(option.value);
		}

		// clear any query string params when the field is changed
		setParams(CLEAR_ALL_QUERY_PARAMS, 'replace');
	};

	const onChartTypeChange = (chartType: SingleSelectChartType) => {
		setSelectedChartType(chartType);
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'radio changed',
			'jira.issueFieldStatistics.sidebarChart',
			{
				chartType,
			},
		);

		// clear any query string params when the field is changed
		setParams(CLEAR_ALL_QUERY_PARAMS, 'replace');
	};

	/*
	 * Extra div wrapper required
	 * refer https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/business/shared/components/app-wrapper/src/ui/app-wrapper/sidebar-layout/styled.tsx#23
	 */
	return (
		<Box>
			<Box xcss={sidebarWrapperStyles}>
				<Flex justifyContent="space-between" alignItems="center" xcss={headingStyles}>
					<Heading size="medium">{formatMessage(messages.heading)}</Heading>
					<Flex gap="space.100">
						<ShareButton selectedFieldId={selectedFieldId} selectedChartType={selectedChartType} />
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label={formatMessage(messages.close)}
							isTooltipDisabled={false}
							onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
								fireUIAnalytics(analyticsEvent, 'closeIssueFieldStatistics');
								onClose();
							}}
						/>
					</Flex>
				</Flex>
				<Stack space="space.250" xcss={mainContentStyles}>
					<p>{formatMessage(messages.info)}</p>
					<Box>
						<Label htmlFor="issue-field-statistics-single-select">
							{formatMessage(messages.selectLabel)}
						</Label>
						<Select
							value={selectFieldValue}
							onChange={onSelectField}
							inputId="issue-field-statistics-single-select"
							options={selectFieldOptions}
						/>
						<StatisticsRenderer
							selectedFieldId={selectedFieldId}
							selectedField={selectedField}
							selectedChartType={selectedChartType}
							onChartTypeChange={onChartTypeChange}
							jql={jql}
						/>
					</Box>
				</Stack>
				<Box xcss={feedbackButtonWrapperStyles}>
					<FeedbackButton />
				</Box>
			</Box>
		</Box>
	);
};

const sidebarWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	paddingInline: 'space.250',
	paddingBlock: 'space.200',
});

const mainContentStyles = xcss({
	flexGrow: 1,
});

const headingStyles = xcss({
	marginBottom: 'space.150',
});

const feedbackButtonWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-start',
	paddingBlock: 'space.100',
});
